import * as THREE from 'three/webgpu';
import type { CarSpec } from '../sim/types';
import type { PoseSnapshot } from './interp';
import type { CarModel } from './car-model';
import type { CodriverAvatar, CodriverMotion } from './codriver-avatar';

export interface CarAnimState {
  /** Road-wheel steering angle (rad, + = left). */
  steerAngle: number;
  handbrake: number;
  brake: number;
  gear: number;
  rpm: number;
  speedKmh: number;
  cockpit: boolean;
  headlights: boolean;
  unitsMph: boolean;
  now: number;
  /** Car accelerations and yaw rate for the co-driver figure (see CodriverMotion). */
  gLat: number;
  gLong: number;
  gVert: number;
  yawRate: number;
  speed: number;
  reduceMotion: boolean;
}

const _q = new THREE.Quaternion();
const _axisZ = new THREE.Vector3(0, 0, 1);
const _axisX = new THREE.Vector3(1, 0, 0);

/**
 * Renders the car from interpolated sim snapshots. Until the GLB model is loaded (or if it
 * fails), a placeholder body is shown so the game stays playable.
 */
export class CarView {
  readonly root = new THREE.Group();
  readonly body = new THREE.Group();
  readonly wheelPivots: THREE.Group[] = [];
  readonly wheelSpinners: THREE.Group[] = [];
  private dashMesh: THREE.Object3D | null | undefined;
  model: CarModel | null = null;
  private readonly spec: CarSpec;
  private placeholder: THREE.Object3D | null = null;
  private readonly tmp = new THREE.Vector3();
  private lastGear = 1;
  private shiftKick = 0;
  /** The co-driver in the passenger seat, once loaded. */
  codriver: CodriverAvatar | null = null;
  private readonly motion: CodriverMotion = { gLat: 0, gLong: 0, gVert: 1, yawRate: 0, speed: 0 };

  constructor(spec: CarSpec) {
    this.spec = spec;
    this.root.add(this.body);
    this.placeholder = this.makePlaceholder();
    this.body.add(this.placeholder);
    for (let i = 0; i < 4; i++) {
      const pivot = new THREE.Group();
      const spinner = new THREE.Group();
      pivot.add(spinner);
      this.root.add(pivot);
      this.wheelPivots.push(pivot);
      this.wheelSpinners.push(spinner);
      spinner.add(this.makePlaceholderWheel());
    }
  }

  private makePlaceholder(): THREE.Object3D {
    const g = new THREE.Group();
    const paint = new THREE.MeshStandardNodeMaterial({ color: 0x0c5fd6, roughness: 0.35, metalness: 0.1 });
    const dark = new THREE.MeshStandardNodeMaterial({ color: 0x111418, roughness: 0.6 });
    const lower = new THREE.Mesh(new THREE.BoxGeometry(1.8, 0.62, 4.02), paint);
    lower.position.set(0, 0.62, 0.02);
    const cabin = new THREE.Mesh(new THREE.BoxGeometry(1.42, 0.5, 2.0), dark);
    cabin.position.set(0, 1.16, -0.4);
    for (const m of [lower, cabin]) {
      m.castShadow = true;
      g.add(m);
    }
    return g;
  }

  private makePlaceholderWheel(): THREE.Object3D {
    const t = this.spec.tyre;
    const geo = new THREE.CylinderGeometry(t.radius, t.radius, t.width, 24);
    geo.rotateZ(Math.PI / 2);
    const m = new THREE.Mesh(geo, new THREE.MeshStandardNodeMaterial({ color: 0x1a1a1a, roughness: 0.9 }));
    m.castShadow = true;
    return m;
  }

  setModel(model: CarModel): void {
    if (this.placeholder) this.body.remove(this.placeholder);
    this.placeholder = null;
    this.model = model;
    this.body.add(model.body);
    if (model.wheels.length === 4) {
      for (let i = 0; i < 4; i++) {
        this.wheelSpinners[i].clear();
        this.wheelSpinners[i].add(model.wheels[i]);
      }
    }
  }

  /** Seat the co-driver at the car's `codriver_seat` mount (his hip point). */
  setCodriver(av: CodriverAvatar, seat: THREE.Vector3): void {
    av.root.position.copy(seat);
    this.body.add(av.root);
    this.codriver = av;
  }

  update(s: PoseSnapshot): void {
    this.body.position.set(s.px, s.py, s.pz);
    this.body.quaternion.set(s.qx, s.qy, s.qz, s.qw);
    const q = this.body.quaternion;
    for (let i = 0; i < 4; i++) {
      const w = this.spec.wheels[i];
      const travel = w.axle === 'front' ? this.spec.suspension.front.travel : this.spec.suspension.rear.travel;
      // Wheel centre = mount (top of travel) − cast distance along the body's up axis.
      const local = this.tmp.set(w.position[0], w.position[1] + travel - s.wheelDist[i], w.position[2]);
      local.applyQuaternion(q).add(this.body.position);
      const pivot = this.wheelPivots[i];
      pivot.position.copy(local);
      pivot.quaternion.copy(q);
      pivot.rotateY(s.wheelSteer[i]);
      // Spin about the axle (car-local X). Positive wheel omega rolls the car forward (+Z).
      this.wheelSpinners[i].rotation.x = s.wheelSpin[i];
    }
  }

  /** Cockpit details: steering wheel, levers, lights, dash display, driver visibility. */
  animate(a: CarAnimState, dt: number): void {
    const m = this.model;
    if (!m) return;
    if (m.steeringWheel) {
      // Steering wheel turns by road-wheel angle × steering ratio (counter-clockwise for left).
      _q.setFromAxisAngle(_axisZ, -a.steerAngle * this.spec.steering.ratio);
      m.steeringWheel.quaternion.copy(m.steeringRest).multiply(_q);
    }
    if (m.handbrake) {
      _q.setFromAxisAngle(_axisX, -0.55 * a.handbrake);
      m.handbrake.quaternion.copy(m.handbrakeRest).multiply(_q);
    }
    if (a.gear !== this.lastGear) {
      this.shiftKick = a.gear > this.lastGear ? 1 : -1;
      this.lastGear = a.gear;
    }
    this.shiftKick *= Math.exp(-dt / 0.09);
    if (m.shifter) {
      _q.setFromAxisAngle(_axisX, this.shiftKick * 0.28);
      m.shifter.quaternion.copy(m.shifterRest).multiply(_q);
    }
    m.brakeLight.value = a.brake > 0.05 ? 1 : 0;
    m.headLight.value = a.headlights ? 1 : 0;
    if (m.driver) m.driver.visible = !a.cockpit;
    // Cockpit-only details (shift lever, handbrake, dash screen) can't be seen from outside: skip
    // their draws and the dash texture upload there.
    if (m.shifter) m.shifter.visible = a.cockpit;
    if (m.handbrake) m.handbrake.visible = a.cockpit;
    this.dashMesh ??= m.body.getObjectByName('batch:display') ?? null;
    if (this.dashMesh) this.dashMesh.visible = a.cockpit;
    if (a.cockpit) m.display?.draw(a.now, a.rpm, this.spec.engine.limiterRpm, a.gear, a.speedKmh, a.unitsMph);
    if (this.codriver) {
      const mo = this.motion;
      mo.gLat = a.gLat;
      mo.gLong = a.gLong;
      mo.gVert = a.gVert;
      mo.yawRate = a.yawRate;
      mo.speed = a.speed;
      this.codriver.update(mo, dt, a.reduceMotion);
    }
  }
}
